import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

/**
 * PrimeVue in modalità tokenizzata: i suoi token puntano alle CSS variables di
 * `assets/tokens.css`, che sono la fonte di verità dei colori.
 * Nessun colore hardcoded qui: solo rimandi a `var(--…)`.
 */
// Mappa comune ai due temi: rimanda ai token, che cambiano da soli (vedi sotto).
const schemaPrimeVue = {
  surface: {
    0: 'var(--color-surface)',
    50: 'var(--color-bg-soft)',
    100: 'var(--color-bg-page)',
    200: 'var(--color-border-soft)',
    300: 'var(--color-border)',
  },
  text: {
    color: 'var(--color-text)',
    mutedColor: 'var(--color-text-secondary)',
  },
  content: {
    borderColor: 'var(--color-border-soft)',
  },
  formField: {
    borderRadius: 'var(--radius-sm)',
    borderColor: 'var(--color-border)',
    // Il focus deve restare sempre visibile.
    focusRing: { color: 'var(--color-primary)', width: '2px', offset: '2px' },
  },
  // Pannelli flottanti (dropdown di Select/AutoComplete, Dialog, Popover,
  // ConfirmDialog/ConfirmPopup). Aura li lega a `surface.700/800/900`, livelli
  // che non copriamo in `surface` sopra: senza questo rimando restano ai grigi
  // di default di Aura, "fuori tema" e molto più scuri del resto in scuro.
  overlay: {
    select: { background: 'var(--color-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text)' },
    popover: { background: 'var(--color-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text)' },
    modal: { background: 'var(--color-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text)' },
  },
  // Voci di lista dentro quei pannelli (opzioni di Select/AutoComplete/
  // Listbox): stesso problema, `surface.100/800` non mappati.
  list: {
    option: {
      focusBackground: 'color-mix(in oklch, var(--color-text) 6%, transparent)',
      selectedBackground: 'var(--surface-accent-soft)',
      selectedFocusBackground: 'var(--surface-accent-selected)',
      color: 'var(--color-text)',
      focusColor: 'var(--color-text)',
      selectedColor: 'var(--color-text)',
      selectedFocusColor: 'var(--color-text)',
      icon: { color: 'var(--color-text-muted)', focusColor: 'var(--color-text-secondary)' },
    },
  },
};

const preset = definePreset(Aura, {
  primitive: {
    // I raggi sono primitive: la coerenza conta più della varietà.
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: 'var(--radius-sm)',
      md: 'var(--radius-md)',
      lg: 'var(--radius-lg)',
      xl: 'var(--radius-lg)',
    },
  },
  semantic: {
    primary: {
      // La scala usa l'accento per le superfici e il primary (più scuro,
      // accessibile su bianco) per testo e bottoni.
      500: 'var(--color-accent)',
      600: 'var(--color-primary)',
      700: 'var(--color-primary-hover)',
    },
    colorScheme: {
      // Chiaro e scuro puntano agli STESSI `var(--…)`: sono i token a
      // ribaltarsi via `[data-theme]` (tokens.css), non queste mappe. Così lo
      // schema PrimeVue resta un unico rimando e i due temi non divergono.
      light: schemaPrimeVue,
      dark: schemaPrimeVue,
    },
  },
  components: {
    datatable: {
      headerCell: {
        // Evidenze dell'header (ordinamento, hover). Aura le lega a token
        // (`highlight.*`, `content.hover.*`) che NON mappiamo: in scuro il testo
        // su hover risolve scuro e sparisce. Le fissiamo ai nostri token, che si
        // ribaltano da sé sui due temi. Sfondi = tint NEUTRO ricavato
        // dall'inchiostro (mai verde: le evidenze restano appena
        // percepibili), testo = quello normale, sempre leggibile. Il colore a
        // riposo resta quello di Aura (già corretto sui due temi): tocchiamo
        // solo gli stati che si rompevano.
        hoverColor: 'var(--color-text)',
        hoverBackground: 'color-mix(in oklch, var(--color-text) 6%, transparent)',
        selectedColor: 'var(--color-text)',
        selectedBackground: 'color-mix(in oklch, var(--color-primary) 5%, transparent)',
      },
      // Freccia di ordinamento: stesso principio, così resta visibile su hover
      // anche in scuro.
      sortIcon: {
        color: 'var(--color-text-secondary)',
        hoverColor: 'var(--color-text)',
      },
    },
  },
});

export const temaEvolfin = {
  preset,
  options: {
    // Le utility Tailwind devono poter vincere sui componenti PrimeVue.
    cssLayer: { name: 'primevue', order: 'theme, base, primevue, utilities' },
    // PrimeVue attiva lo schema scuro sotto lo stesso attributo che pilota i
    // token: `data-theme='dark'` sull'`<html>` (impostato dallo store `tema`).
    darkModeSelector: "[data-theme='dark']",
  },
};
